<script setup>
import FormLabel from "./Label.vue";
import FormErrors from "./Errors.vue";
import _uniqueId from "lodash/uniqueId";
import { computed, ref, useSlots } from "vue";
import { EyeIcon, EyeSlashIcon } from "@heroicons/vue/20/solid";
import { FormHelp, FormHint } from ".";
import { twMerge } from "tailwind-merge";

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: "",
    },
    label: {
        type: String,
        default: undefined,
    },
    hint: {
        type: String,
    },
    help: {
        type: String,
    },
    z: {
        type: Number,
        default: 0,
    },
    required: {
        type: Boolean,
        default: false,
    },
    errors: {
        type: Array,
        default: [],
    },
    sensitive: {
        type: Boolean,
        default: false,
    },
});
defineEmits(["update:modelValue"]);

const slots = useSlots();

const key = _uniqueId("input-");
const showSensitive = ref(props.modelValue?.length === 0);

const toggleShowSensitive = () => {
    showSensitive.value = !showSensitive.value;
};

const inputClasses = computed(() => {
    let classes = [
        "block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset text-sm leading-6",
        "text-gray-900 bg-white ring-gray-300 focus:ring-gray-950 placeholder:text-gray-400",
    ];
    switch (props.z) {
        case 3:
            classes.push("dark:text-white dark:bg-gray-600 dark:ring-gray-500 dark:focus:ring-gray-400 dark:placeholder:text-gray-300");
            break;
        case 2:
            classes.push("dark:text-white dark:bg-gray-700 dark:ring-gray-600 dark:focus:ring-gray-500 dark:placeholder:text-gray-400");
            break;
        case 1:
            classes.push("dark:text-white dark:bg-gray-800 dark:ring-gray-700 dark:focus:ring-gray-600 dark:placeholder:text-gray-400");
            break;
        default:
            classes.push("dark:text-white dark:bg-gray-900 dark:ring-gray-800 dark:focus:ring-gray-700 dark:placeholder:text-gray-400");
            break;
    }

    if (props.sensitive) {
        classes.push("pr-12");
    }

    if (props.errors.length > 0) {
        classes.push("ring-red-500 focus:ring-red-500 dark:ring-red-500 dark:focus:ring-red-500");
    }

    return twMerge(classes);
});

const iconClasses = computed(() => {
    let classes = ["w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-500"];

    switch (props.z) {
        case 3:
            classes.push("dark:text-gray-300 hover:dark:text-gray-100");
            break;
        case 2:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
        case 1:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
        default:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
    }

    return twMerge(classes);
});
</script>

<template>
    <div class="flex flex-col">
        <div :class="[label || !!slots.label ? 'justify-between' : 'justify-end', 'flex items-center space-x-3']">
            <FormLabel v-if="label || !!slots.label" :for="key">
                <slot name="label">{{ label }}</slot>
            </FormLabel>
            <slot name="hint">
                <FormHint v-if="hint" :z="z">
                    {{ hint }}
                </FormHint>
            </slot>
        </div>

        <div :class="[label || !!slots.label || hint || !!slots.hint ? 'mt-2' : '']">
            <div class="relative">
                <input
                    :value="modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    :type="sensitive && !showSensitive ? 'password' : 'text'"
                    :id="key"
                    :required="required"
                    v-bind="$attrs"
                    :class="inputClasses" />
                <div v-if="sensitive" class="absolute inset-y-0 right-0 flex items-center mr-4">
                    <button type="button" v-if="showSensitive" @click="toggleShowSensitive">
                        <EyeSlashIcon :class="iconClasses" />
                    </button>
                    <button type="button" v-else @click="toggleShowSensitive">
                        <EyeIcon :class="iconClasses" />
                    </button>
                </div>
                <template v-if="!!slots.action">
                    <slot name="action"></slot>
                </template>
            </div>
        </div>

        <slot name="help">
            <FormHelp v-if="help" class="mt-2">
                {{ help }}
            </FormHelp>
        </slot>

        <FormErrors v-if="errors" class="mt-2" :errors="errors" />
    </div>
</template>
